import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, MapPin, Phone, Mail, GraduationCap, Linkedin, Send, FileText, Trophy } from 'lucide-react';

import InteractiveCursorBackground from './background';

const THEME_CONFIG = {
  primary: '#00FF41',
  background: 'transparent', // Make main app background transparent
  canvasBg: '#0a0e1a',        // The actual dark color used in your canvas script
  accent: '#22D3EE',
  glass: 'rgba(11, 14, 20, 0.8)' // Darker, semi-transparent glass for readability
};

const projects = [
  {
    title: 'A2C (Anything To CAN)',
    org: 'VT BOLT',
    image: '/A2C_cover.png',
    subtitle: 'Universal Communication Converter & Data Node',
    tech: ['STM32G4', 'CAN FD', 'Altium Designer', 'Embedded C', 'Hardware Hashing'],
    shortDesc: 'A protocol-agnostic embedded node featuring automatic CAN ID negotiation and multi-peripheral data packetization.',
    content: [
      { 
        type: 'paragraph', 
        text: 'The A2C (Anything to CAN) is a modular STM32G4-based embedded node designed for deployment on a distributed CAN FD network. Originally developed for an electric motorcycle, the system functions as a general-purpose data acquisition bridge, interfacing with SPI, I2C, UART, and analog peripherals to standardize raw data into CAN FD frames.' 
      },
      { 
        type: 'heading', 
        text: 'Network Architecture & ID Hashing' 
      },
      { 
        type: 'paragraph', 
        text: 'A core objective was "plug-and-play" deployment: multiple identical nodes must coexist on the bus without per-device firmware configuration. I implemented a deterministic hashing mechanism seeded from the STM32’s unique 96-bit hardware ID. During startup, nodes execute a handshake phase; if a collision is detected, the node re-hashes until a unique 11-bit identifier is confirmed, transitioning then to a lightweight, transmit-only operational mode.' 
      },
      { 
        type: 'heading', 
        text: 'Design Process' 
      },
      { 
        type: 'paragraph', 
        text: 'The PCB was developed in Altium Designer, beginning with a full schematic capture focused on power distribution and signal integrity for high-speed communication. The project moved through two distinct layout phases:' 
      },
      { 
        type: 'list', 
        items: [
          'Initial Iteration: A proof-of-concept focused on electrical correctness and interface validation. This version confirmed that all power domains and signal paths were functional but exceeded size constraints.',
          'Final Iteration: A high-density refinement utilizing both sides of the PCB. Trace lengths were minimized and components reorganized to achieve a significantly smaller footprint suitable for space-constrained vehicle mounting.'
        ]
      },
      { 
        type: 'image', 
        url: ' A2C_comparison.png', 
        caption: 'Side-by-side comparison of the initial PoC layout and the final optimized high-density design.' 
      },
      { 
        type: 'heading', 
        text: 'Manufacturing' 
      },
      { 
        type: 'paragraph', 
        text: 'The design is currently finalized and DFM (Design for Manufacturing) checked within Altium. It is staged for fabrication, with component selection favoring surface mount packages to ensure reliability under high-vibration conditions.' 
      },
      { 
        type: 'image', 
        url: '/images/fina2c.jpg', 
        caption: 'Final assembled Product (not yet manufactured but is in shipping).' 
      },
      { 
        type: 'heading', 
        text: 'Testing & Deployment' 
      },
      { 
        type: 'paragraph', 
        text: 'As of right now the design has not been manufactured so it has not been tested or deployed. This will be resolved as soon as the manufacturing stages are complete.' 
      }
    ],
    appendix: [
      { url: '/A2C_back.png', title: 'Final Back view' },
      { url: '/A2C_old.png', title: 'First Design' },
      { url: '/A2C_cover.png', title: 'Final Design' },
      { url: '/A2C_sch_1.png', title: 'Schematic Image' },
      { url: '/A2C_sch_2.png', title: 'Schematic Image' },
      { url: '/A2C_sch_3.png', title: 'Schematic Image' },
      { url: '/A2C_sch_4.png', title: 'Schematic Image' },
      { url: '/A2C_sch_5.png', title: 'Schematic Image' },
      { url: '/A2C_sch_6.png', title: 'Schematic Image' },
      { url: '/A2C_sch_7.png', title: 'Schematic Image' }
    ]
  },
  {
    title: 'Battery Testbench',
    org: 'VT BOLT',
    image: '/Testbench_cover.png',
    subtitle: '14S Lithium-Ion Discharge Monitoring System',
    tech: ['STM32F0', 'L9963E BMIC', 'Altium Designer', 'Precision Analog', 'Relay Logic'],
    shortDesc: 'A high-voltage battery testing platform featuring an L9963E front-end and multi-channel relay load control.',
    content: [
      { 
        type: 'paragraph', 
        text: 'I designed and manufactured an STM32F0-based battery testbench to verify discharge rates and thermal profiles of custom lithium-ion packs. The system supports up to 14 cells in series (max 4.6V/cell), utilizing an ST L9963E Battery Monitoring IC for high-precision voltage acquisition via standard SPI.' 
      },
      { 
        type: 'heading', 
        text: 'Design Process & Technical Pivot' 
      },
      { 
        type: 'paragraph', 
        text: 'The project evolved through four distinct iterations, serving as both a technical development cycle and a leadership exercise for the BOLT team:' 
      },
      { 
        type: 'list', 
        items: [
          'Iteration 1 & 2 (Conceptual): Educational layouts designed to familiarize team members with PCB workflows and component grouping.',
          'Iteration 3 (Engineering Audit): Conducted a rigorous feasibility review focusing on high-voltage clearances and manufacturing constraints. This review revealed that the original ADC-multiplexed tap architecture was prone to electrical degradation and inaccuracy under 14S voltages.',
          'Final Iteration (Production): A total architectural redesign replacing the MUX scheme with the L9963E BMIC. This iteration integrated specialized signal conditioning, Hall-effect current sensing, and analog thermistor multiplexing.'
        ]
      },
      { 
        type: 'image', 
        url: '/Testbench_1_2.png', 
        caption: 'Desgin 1 and 2.' 
      },
      { 
        type: 'image', 
        url: '/Testbench_3_4.png', 
        caption: 'Desgin 3 and 4.' 
      },
      { 
        type: 'heading', 
        text: 'Safety and Load Control' 
      },
      { 
        type: 'paragraph', 
        text: 'To ensure laboratory safety, the board manages four independent high-current discharge paths. I designed the logic to propagate control signals to an external heavy-duty relay board, isolating the high-current switching noise from the sensitive logic. The analog system uses a stable wall-powered reference voltage to ensure ADC consistency regardless of the battery’s state of charge.' 
      },
      { 
        type: 'heading', 
        text: 'Manufacturing & Verification' 
      },
      { 
        type: 'paragraph', 
        text: 'The final board was assembled using SMD reflow soldering and targeted manual assembly for power connectors. Current work is focused on firmware bring-up, specifically validating the SPI communication timing with the L9963E and verifying the voltage-divider accuracy for the thermistor array.' 
      },
      { 
        type: 'image', 
        url: '/testbench-assembled-pcb.png', 
        caption: 'Final assembled Product (not yet manufactured but is in shipping).' 
      }
    ],
    appendix: [
      { url: '/Testbench_1.png', title: 'First Design' },
      { url: '/Testbench_2.png', title: 'Second Design' },
      { url: '/Testbench_3.png', title: 'Third Design' },
      { url: '/Testbench_cover.png', title: 'Final Design' },
      { url: '/Testbench_back.png', title: 'Final back' },
      { url: '/Testbench_sch_1.png', title: 'Schematic Image' },
      { url: '/Testbench_sch_2.png', title: 'Schematic Image' },
      { url: '/Testbench_sch_3.png', title: 'Schematic Image' },
      { url: '/Testbench_sch_4.png', title: 'Schematic Image' },
      { url: '/Testbench_sch_5.png', title: 'Schematic Image' },
      { url: '/Testbench_sch_6.png', title: 'Schematic Image' },
      { url: '/Testbench_sch_7.png', title: 'Schematic Image' },
      { url: '/Testbench_sch_8.png', title: 'Schematic Image' },
      { url: '/Testbench_sch_9.png', title: 'Schematic Image' }
    ]
  },
  {
    title: 'Internal Knowledge Wiki',
    org: 'VT BOLT',
    image: '/Wiki_cover.png', 
    subtitle: 'Full-Stack Technical Documentation Platform',
    tech: ['React.js', 'Node.js', 'Express', 'MongoDB', 'REST API', 'Regex'],
    shortDesc: 'A custom-built, block-based CMS designed to centralize technical subteam documentation through a modular UI.',
    content: [
      { 
        type: 'paragraph', 
        text: 'I architected and developed a from-scratch internal wiki to solve the challenge of fragmented technical knowledge across BOLT subteams. Acting as the sole developer, I built a full-stack proof-of-concept that allowed members to create, index, and search complex engineering documentation through a modular, card-based interface.' 
      },
      { 
        type: 'heading', 
        text: 'Full-Stack Architecture' 
      },
      { 
        type: 'paragraph', 
        text: 'The system was built on a MERN-style stack, prioritizing a flexible data structure that could evolve with the team’s documentation needs:' 
      },  
      { 
        type: 'list', 
        items: [
          'Frontend: Developed in React.js, the UI utilized a dynamic component-rendering engine to display various media types within a single page view.',
          'Backend: A Node.js and Express REST API managed the CRUD operations for page data, with MongoDB serving as the document store for high-flexibility content schemas.',
          'Search Logic: Implemented a keyword discovery system using RegEx-based pattern matching to query the database, allowing users to find technical specs and assembly instructions across all subteam directories.'
        ]
      },
      { 
        type: 'heading', 
        text: 'Design: Block-Based Content Editor' 
      },
      { 
        type: 'paragraph', 
        text: 'The core of the platform was a modular "Card-Based" editor. Rather than a static text area, the interface allowed users to select specific element types—such as headers, code snippets, or image galleries—as discrete cards. These cards could be dynamically added, removed, or reordered, providing a "drag-and-drop" feel that enabled non-technical users to build professional-grade documentation without direct code interaction.' 
      },
      { 
        type: 'heading', 
        text: 'Manufacturing & Engineering Retrospective' 
      },
      { 
        type: 'paragraph', 
        text: 'The "manufacturing" of the software involved creating a secure administrative layer and a standardized API for content delivery. Although the system was fully functional, the project provided a vital lesson in the "Build vs. Buy" trade-off. Following a review of the long-term maintenance and security overhead required for a custom web application, the team opted to explore established frameworks like Wiki.js or Obsidian for future iterations.' 
      },
      { 
        type: 'heading', 
        text: 'Testing & Deployment' 
      },
      { 
        type: 'paragraph', 
        text: 'Internal testing validated the API’s ability to handle complex nested page structures and verified the responsiveness of the RegEx search logic. While the platform reached a functional proof-of-concept stage, it was not formally deployed due to shifting priorities toward leveraging existing community-supported documentation tools.' 
      }
    ],
    appendix: [
    ]
  }
];

const experience = [
  {
    title: 'Junior Electrical Team Lead',
    organization: 'VT BOLT',
    logo: '/BOLT_logo.png',
    period: 'Sep 2024 - Present',
    location: 'Blacksburg, VA',
    shortDesc: 'Leading hardware development for electric vehicle racing team',
    details: [
      'Leadership for hardware development across all electrical subsystems',
      'Managed a team of 60 members across electrical and mechanical disciplines',
      'Led comprehensive testing for battery and controls subsystems',
      'Managed all electrical and mechanical documentation and version control',
      'Coordinated cross-functional integration between electrical, mechanical, and software teams',
      'Oversaw PCB design and manufacturing for critical vehicle systems',
      'Implemented rigorous testing protocols for high-voltage systems'
    ]
  },
  {
    title: 'Simulation Design Team',
    organization: 'Hume Research Center',
    logo: '/Hume_logo.png',
    period: 'Sep 2024 - May 2025',
    location: 'Virginia Tech',
    shortDesc: 'Developing UAV simulation and visualization systems',
    details: [
      'Developed MATLAB simulations for UAV flight paths and obstacle avoidance algorithms',
      'Implemented Python-based drone environments using VisPy for real-time visualization',
      'Created performance metrics and analysis tools for autonomous navigation',
      'Collaborated with research team to validate simulation accuracy',
      'Optimized rendering pipeline for high-framerate 3D visualization',
      'Integrated sensor models for realistic environment simulation'
    ]
  }
];

const skills = {
  technical: ['Schematic Design', 'PCB Layout', 'SMD Soldering', 'CAD', 'FDM 3D Printing'],
  software: ['Altium Designer', 'MATLAB', 'Fusion360', 'AutoCAD', 'Git/Github/Gitlab', 'Quartus'],
  programming: ['C', 'C++', 'Java', 'JavaScript', 'HTML/CSS', 'Python'],
};

const coursework = [
  'Digital Systems', 
  'Embedded Systems', 
  'Computer Architecture', 
  'Data Structures & Algorithms',
  'Signals and Systems',
  'Electronic Circuits'
];

const achievements = [
  { 
    year: '2024', 
    place: '3rd Place', 
    event: 'TSA Nationals - Structural Engineering',
    details: 'Optimized high-efficiency Balsa wood truss systems. Leveraged insights from previous years to minimize prototyping cycles while maintaining high structural reliability. Although unable to attend in person due to an accident, my design and technical documentation led a stand-in teammate to a 3rd place national finish, demonstrating robust design communication and reproducibility.'
  },
  { 
    year: '2023', 
    place: '1st Place', 
    event: 'TSA Nationals - Structural Engineering',
    details: 'Achieved peak structural efficiency by designing an 18.5g Balsa bridge that sustained a 400lb load. Conducted an exhaustive R&D phase involving destructive testing of over 50 prototypes to master the non-uniformity of Balsa wood, strategically placing higher-density members in high-compression zones.'
  },
  { 
    year: '2022', 
    place: '3rd Place', 
    event: 'TSA Nationals - Structural Engineering',
    details: 'Engineered a 150lb load-bearing structure using high-density Basswood. Iterated the design 10+ times to account for the increased mass and longer production requirements of Basswood compared to Balsa. Balanced material hardness with precise joint bonding to maximize the strength-to-weight ratio in a high-stress national competition environment.'
  }
];

function AppendixSection({ images, onImageClick }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={styles.appendixContainer}>
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        style={styles.appendixToggle}
      >
        <span>Technical Appendix ({images.length} Images)</span>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
          <ChevronRight size={20} />
        </motion.div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={styles.appendixGrid}>
              {images.map((img, i) => (
                <motion.div 
                  key={i} 
                  style={styles.appendixItem}
                  whileHover={{ scale: 1.05 }}
                  onClick={() => onImageClick(img)}
                >
                  <img src={img.url} alt={img.title} style={styles.appendixImage} />
                  <p style={styles.appendixImageTitle}>{img.title}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [currentProject, setCurrentProject] = useState(0);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (selectedDetail || selectedImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedDetail, selectedImage]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = () => {
    window.location.href = 'mailto:alexander.j.bloom925@gmail.com';
  };

  return (
    <div style={styles.app}>
      <InteractiveCursorBackground />
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          overflow-x: hidden;
          background-color: ${THEME_CONFIG.background};
        }
        html {
          scroll-behavior: smooth;
          font-size: 16px;
        }
        
        @media (min-width: 2000px) {
          html { font-size: 20px; }
        }

        @media (max-width: 1024px) {
          .hero-wrapper {
            flex-direction: column !important;
            text-align: center;
            padding-top: 4rem;
          }
          .glass-box {
            max-width: 100% !important;
            width: 90% !important;
          }
          .image-container {
            max-width: 60% !important;
            min-width: 280px !important;
          }
        }

        .modal-body-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .modal-body-scroll::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }
        .modal-body-scroll::-webkit-scrollbar-thumb {
          background: ${THEME_CONFIG.primary};
          border-radius: 10px;
        }
      `}</style>

      <section style={styles.hero} id="hero">
        <div style={styles.heroWrapper} className="hero-content">
          <motion.div 
            style={styles.imageContainer}
            initial={{ opacity: 0, scale: 0.25 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className='hero-image-glow'
          >
            <div style={styles.imagePlaceholder}>
              <span style={styles.initials}>AB</span>
            </div>
          </motion.div>

          <motion.div 
            style={styles.glassBoxAbsolute}
            className="glass-box-absolute"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h1 style={styles.heroName}>Alexander Bloom</h1>
            <div style={styles.heroLocation}>
              <MapPin size={16} color={THEME_CONFIG.accent} />
              <span>Blacksburg, VA</span>
            </div>
            <div style={styles.heroContacts}>
              <div style={styles.contactItem}>
                <Phone size={14} />
                <span>(804) 528-8540</span>
              </div>
              <div style={styles.contactItem}>
                <Mail size={14} />
                <span>alexander.j.bloom925@gmail.com</span>
              </div>
            </div>
            <div style={styles.heroEducation}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <GraduationCap size={18} color={THEME_CONFIG.primary} />
                <img 
                  src="VT_logo.png" 
                  alt="Virginia Tech" 
                  style={{ width: '40px', height: '40px', objectFit: 'contain' }} 
                />
              </div>
              <div>
                <p style={styles.eduText}>B.S. Electrical and Computer Engineering</p>
                <p style={styles.eduSubtext}>Chip-Scale Integration</p>
                <p style={styles.eduSubtext}>Virginia Tech • GPA: 3.13</p>
              </div>
            </div>
            <div style={styles.heroActionArea}>
              <motion.a
                href="/resume.pdf"
                download="Alexander_Bloom_Resume.pdf"
                style={styles.resumeBtn}
                whileHover={{ scale: 1.05, backgroundColor: `${THEME_CONFIG.primary}20` }}
                whileTap={{ scale: 0.95 }}
              >
                <FileText size={18} />
                <span>Download Resume</span>
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {<div style={styles.navbarSpacer} />}

      <motion.nav 
        layout
        style={{
          ...styles.navbar,
          ...(scrolled ? styles.navbarFixed : styles.navbarBottom)
        }}
        transition={{ 
          layout: { duration: 0.6, type: "spring", stiffness: 200, damping: 25 },
          opacity: { duration: 0.2 } 
        }}
      >
        <motion.div 
          style={{
            ...styles.navbarInner,
            justifyContent: scrolled ? 'space-between' : 'center'
          }}
          animate={{ width: scrolled ? '95%' : 'auto' }}
        >
          <AnimatePresence>
            {scrolled && (
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onClick={() => scrollToSection('hero')}
                style={styles.navBrand}
                whileHover={{ scale: 1.05, color: '#ffffff' }}
              >
                Alexander Bloom
              </motion.button>
            )}
          </AnimatePresence>

          <div style={{ display: 'flex', gap: '5px' }}>
            {['Projects', 'Experience', 'Skills', 'Coursework', 'Achievements', 'Contact'].map((item) => (
              <motion.button
                key={item}
                style={styles.navButton}
                onClick={() => scrollToSection(item.toLowerCase())}
                whileHover={{ 
                  y: -2, 
                  textShadow: `0 0 8px ${THEME_CONFIG.primary}`,
                  transition: { type: "spring", stiffness: 900 } 
                }}
                whileTap={{ scale: 0.95 }}
              >
                {item}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </motion.nav>

      <section style={styles.section} id="projects">
        <div style={styles.container}>
          <motion.h2 
            style={styles.sectionTitle}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Featured Projects
          </motion.h2>
          
          <div style={styles.projectSlider}>
            <button 
              style={styles.sliderButton} 
              onClick={() => setCurrentProject((currentProject - 1 + projects.length) % projects.length)}
            >
              <ChevronLeft size={24} />
            </button>

            <div style={{ position: 'relative', width: '100%', maxWidth: '700px' }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentProject}
                  style={styles.projectCard}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => setSelectedDetail({ type: 'project', data: projects[currentProject] })}
                  whileHover={{ 
                    boxShadow: `0 0 25px ${THEME_CONFIG.primary}40`,
                    borderColor: THEME_CONFIG.primary,
                    y: -5 
                  }}
                >
                  <div style={styles.projectImageContainer}>
                    <img 
                      src={projects[currentProject].image} 
                      alt={projects[currentProject].title}
                      style={styles.projectImage}
                    />
                    <div style={styles.imageOverlay} />
                  </div>

                  <div style={styles.projectCardContent}>
                    <span style={styles.projectOrgTag}>{projects[currentProject].org}</span>

                    <h3 style={styles.projectTitle}>{projects[currentProject].title}</h3>
                    <p style={styles.projectSubtitle}>{projects[currentProject].subtitle}</p>
                    <p style={styles.projectShortDesc}>{projects[currentProject].shortDesc}</p>
                    
                    <div style={styles.projectTechTags}>
                      {projects[currentProject].tech.map((tech) => (
                        <span key={tech} style={styles.hashTag}>#{tech.toLowerCase().replace(/\s+/g, '')}</span>
                      ))}
                    </div>
                    
                    <button style={styles.expandProjectBtn}>View Full Details →</button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <button 
              style={styles.sliderButton} 
              onClick={() => setCurrentProject((currentProject + 1) % projects.length)}
            >
              <ChevronRight size={24} />
            </button>
          </div>

          <div style={styles.sliderIndicators}>
            {projects.map((_, idx) => (
              <button
                key={idx}
                style={{
                  ...styles.indicator,
                  ...(idx === currentProject ? styles.indicatorActive : {})
                }}
                onClick={() => setCurrentProject(idx)}
              />
            ))}
          </div>
        </div>
      </section>

      <section style={styles.section} id="experience">
        <div style={styles.container}>
          <motion.h2 
            style={styles.sectionTitle}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Experience
          </motion.h2>
          <div style={styles.timeline}>
            {experience.map((exp, idx) => (
              <motion.div
                key={idx}
                style={styles.timelineItem}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                onClick={() => setSelectedDetail({ type: 'experience', data: exp })}
                whileHover={{ x: 5 }}
              >
                <div style={styles.timelineDot} />
                <div style={styles.timelineCard}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                    {exp.logo && (
                      <img 
                        src={exp.logo} 
                        alt={`${exp.organization} logo`} 
                        style={styles.expLogo} 
                      />
                    )}
                    <div>
                      <h3 style={styles.expTitle}>{exp.title}</h3>
                      <p style={styles.expOrg}>{exp.organization}</p>
                    </div>
                  </div>
                  <p style={styles.expPeriod}>{exp.period}</p>
                  <p style={styles.expShortDesc}>{exp.shortDesc}</p>
                  <button style={styles.viewDetailsBtn}>Click for details →</button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section style={styles.section} id="skills">
        <div style={styles.container}>
          <motion.h2 
            style={styles.sectionTitle}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Skills
          </motion.h2>
          
          <div style={styles.skillsGrid}>
            {Object.entries(skills).map(([category, items], idx) => (
              <motion.div 
                key={category} 
                style={styles.skillCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ borderColor: THEME_CONFIG.primary, y: -5 }}
              >
                <h3 style={styles.skillCategory}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </h3>
                <div style={styles.skillDivider} />
                <ul style={styles.bulletList}>
                  {items.map((skill) => (
                    <li key={skill} style={styles.bulletItem}>
                      <span style={{ color: THEME_CONFIG.primary, marginRight: '8px' }}>▹</span>
                      {skill}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section style={styles.section} id="coursework">
        <div style={styles.container}>
          <motion.h2 
            style={styles.sectionTitle}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Coursework
          </motion.h2>
          <div style={styles.skillsCourseworkGrid}>
            <div style={styles.skillCard}>
              <h3 style={styles.skillCategory}>Relevant Courses</h3>
              <ul style={styles.bulletList}>
                {coursework.map((course) => (
                  <li key={course} style={styles.bulletItem}>{course}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.section} id="achievements">
        <div style={styles.container}>
          <motion.h2 
            style={styles.sectionTitle}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Achievements
          </motion.h2>
          <div style={styles.achievementsGrid}>
            {achievements.map((ach, idx) => (
              <motion.div
                key={idx}
                style={styles.achievementCard}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.03 }}
                onClick={() => setSelectedDetail({ type: 'achievement', data: ach })}
              >
                <Trophy size={24} color={THEME_CONFIG.primary} style={{ marginBottom: '10px' }} />
                <div style={styles.achievementYear}>{ach.year}</div>
                <div style={styles.achievementPlace}>{ach.place}</div>
                <p style={styles.achievementEvent}>{ach.event}</p>
                <span style={{ fontSize: '0.8rem', color: THEME_CONFIG.primary, marginTop: '10px', display: 'block' }}>Read More →</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section style={styles.section} id="contact">
        <div style={styles.container}>
          <motion.h2 
            style={styles.sectionTitle}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Contact Me
          </motion.h2>
          <motion.div
            style={styles.contactSection}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div style={styles.contactLinks}>
              <div style={styles.contactLinkItem}>
                <Mail size={20} color={THEME_CONFIG.primary} />
                <a href="mailto:alexander.j.bloom925@gmail.com" style={styles.contactLink}>
                  alexander.j.bloom925@gmail.com
                </a>
              </div>
              <div style={styles.contactLinkItem}>
                <Linkedin size={20} color={THEME_CONFIG.accent} />
                <a href="https://linkedin.com/in/abloom925" target="_blank" rel="noopener noreferrer" style={styles.contactLink}>
                  linkedin.com/in/abloom925
                </a>
              </div>
            </div>
            <motion.button
              style={styles.sendMessageBtn}
              onClick={handleSendMessage}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Send size={18} />
              <span>Send Message</span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      <footer style={styles.footer}>
        <div style={styles.container}>
          <div style={styles.footerDivider} />
          <div style={styles.footerContent}>
            <div style={styles.footerLeft}>
              <h3 style={styles.footerLogo}>AB</h3>
              <p style={styles.footerTagline}>Building the future of Electrical & Computer Engineering.</p>
            </div>
            
            <div style={styles.footerRight}>
              <div style={styles.footerSocials}>
                <a href="https://linkedin.com/in/abloom925" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>LinkedIn</a>
                <a href="mailto:alexander.j.bloom925@gmail.com" style={styles.footerLink}>Email</a>
              </div>
              <p style={styles.copyright}>
                © {new Date().getFullYear()} Alexander Bloom. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {selectedDetail && (
          <motion.div
            style={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedDetail(null)}
          >
            <motion.div
              style={styles.modalContent}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <div>
                  <h2 style={styles.modalTitle}>
                    {selectedDetail.type === 'experience' 
                      ? selectedDetail.data.title 
                      : selectedDetail.type === 'achievement'
                      ? selectedDetail.data.event
                      : selectedDetail.data.title}
                  </h2>
                  <p style={styles.modalSubtitle}>
                    {selectedDetail.type === 'experience' 
                      ? selectedDetail.data.organization 
                      : selectedDetail.type === 'achievement'
                      ? `${selectedDetail.data.year} - ${selectedDetail.data.place}`
                      : selectedDetail.data.subtitle}
                  </p>
                </div>
                <button style={styles.closeButton} onClick={() => setSelectedDetail(null)}>
                  <X size={24} />
                </button>
              </div>

              <div style={styles.modalBody} className="modal-body-scroll">
                {selectedDetail.type === 'experience' ? (
                  <>
                    <p style={styles.modalPeriod}>{selectedDetail.data.period}</p>
                    <p style={styles.modalLocation}>{selectedDetail.data.location}</p>
                    <h3 style={styles.detailsHeading}>Responsibilities & Achievements</h3>
                    <ul style={styles.detailsList}>
                      {selectedDetail.data.details.map((detail, i) => (
                        <li key={i} style={styles.detailItem}>{detail}</li>
                      ))}
                    </ul>
                  </>
                ) : selectedDetail.type === 'achievement' ? (
                  <>
                    <p style={styles.projectFullDesc}>{selectedDetail.data.details}</p>
                  </>
                ) : (
                  <>
                    <div style={styles.projectTechTags}>
                      {selectedDetail.data.tech.map((tech) => (
                        <span key={tech} style={styles.hashTag}>#{tech.toLowerCase().replace(/\s+/g, '')}</span>
                      ))}
                    </div>

                    <div style={styles.dynamicContent}>
                      {selectedDetail.data.content.map((block, index) => {
                        if (block.type === 'heading') return <h3 key={index} style={styles.detailsHeading}>{block.text}</h3>;
                        if (block.type === 'paragraph') return <p key={index} style={styles.projectFullDesc}>{block.text}</p>;
                        if (block.type === 'image') return (
                          <div key={index} style={styles.inlineImageContainer}>
                            <img src={block.url} alt="Project detail" style={styles.inlineImage} />
                            {block.caption && <span style={styles.imageCaption}>{block.caption}</span>}
                          </div>
                        );
                        if (block.type === 'list') return (
                          <ul key={index} style={styles.detailsList}>
                            {block.items.map((item, i) => (
                              <li key={i} style={styles.detailItem}>{item}</li>
                            ))}
                          </ul>
                        );
                        return null;
                      })}
                    </div>

                    {selectedDetail.data.appendix && selectedDetail.data.appendix.length > 0 && (
                      <AppendixSection 
                        images={selectedDetail.data.appendix} 
                        onImageClick={setSelectedImage}
                      />
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            style={styles.imageModalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              style={styles.imageModalContent}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                style={styles.imageModalClose} 
                onClick={() => setSelectedImage(null)}
              >
                <X size={24} />
              </button>
              <img 
                src={selectedImage.url} 
                alt={selectedImage.title} 
                style={styles.imageModalImg} 
              />
              <p style={styles.imageModalCaption}>{selectedImage.title}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const styles = {
  app: {
    backgroundColor: THEME_CONFIG.background,
    color: '#ffffff',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    width: '100%',
    overflowX: 'hidden',
  },
  hero: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(2rem, 5vh, 4rem) 5%',
    background: THEME_CONFIG.background,
    overflow: 'hidden',
  },
  heroWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '1400px',
    width: '100%',
    flexWrap: 'wrap',
    position: 'relative',
  },
  imageContainer: {
    flex: '1 1 400px',
    maxWidth: '550px',
    position: 'relative',
    zIndex: 1,
  },
  imagePlaceholder: {
    width: '100%',
    aspectRatio: '1',
    background: `linear-gradient(135deg, ${THEME_CONFIG.primary}30, ${THEME_CONFIG.accent}30)`,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px solid ${THEME_CONFIG.primary}50`,
    boxShadow: '0 20px 60px rgba(0, 255, 65, 0.2)',
  },
  initials: {
    fontSize: 'clamp(4rem, 12vw, 10rem)',
    fontWeight: 'bold',
    color: THEME_CONFIG.primary,
    opacity: 0.7,
  },
  glassBoxAbsolute: {
    flex: '1 1 450px',
    maxWidth: '550px',
    height: 'fit-content', 
    maxHeight: 'min(800px, 90vh)',
    background: THEME_CONFIG.glass,
    backdropFilter: 'blur(30px)',
    WebkitBackdropFilter: 'blur(30px)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '12px',
    padding: 'clamp(1.25rem, 3vw, 2rem)', 
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    marginLeft: '-8%', 
    zIndex: 10,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem', 
  },
  heroName: {
    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
    fontWeight: 'bold',
    marginBottom: '0.25rem',
    background: `linear-gradient(135deg, ${THEME_CONFIG.primary}, ${THEME_CONFIG.accent})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1.1,
  },
  heroLocation: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: 'clamp(1rem, 2vw, 1.3rem)',
    color: '#d0d0d0',
    marginBottom: '1rem',
  },
  heroContacts: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    marginBottom: '1rem',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    fontSize: 'clamp(0.9rem, 1.8vw, 1.1rem)',
    color: '#d0d0d0',
  },
  heroEducation: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    background: 'rgba(0, 255, 65, 0.05)',
    borderRadius: '8px',
    border: `1px solid ${THEME_CONFIG.primary}20`,
  },
  eduText: {
    fontSize: 'clamp(1rem, 2vw, 1.2rem)',
    color: '#ffffff',
    fontWeight: '500',
    marginBottom: '0.2rem',
  },
  eduSubtext: {
    fontSize: 'clamp(0.85rem, 1.5vw, 1rem)',
    color: '#a0a0a0',
  },
  heroActionArea: {
    marginTop: '1.25rem',
    display: 'flex',
    gap: '1rem',
  },
  resumeBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1rem 2rem',
    background: 'transparent',
    color: THEME_CONFIG.primary,
    border: `1px solid ${THEME_CONFIG.primary}`,
    borderRadius: '4px',
    cursor: 'pointer',
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: '600',
  },
  navBrand: {
    backgroundColor: 'transparent',
    border: 'none',
    color: THEME_CONFIG.primary,
    fontSize: '0.9rem',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    cursor: 'pointer',
    fontFamily: 'monospace', // Gives it a "tech/eng" feel
    padding: '0 20px',
    marginRight: 'auto', // This creates the offset from the rest of the buttons
    display: 'flex',
    alignItems: 'center',
    textShadow: `0 0 10px ${THEME_CONFIG.primary}40`, // Subtle glow
  },
  navbarSpacer: {
    height: '80px',
    width: '100%'
  },
  navbar: {
    position: 'fixed',
    zIndex: 1000,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '0 1rem',
    left: 0
  },
  navbarFixed: {
    top: 0,
    padding: '1rem',
    pointerEvents: 'auto',
  },
  navbarBottom: {
    bottom: '2rem',
    pointerEvents: 'auto',
  },
  navbarInner: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.5rem',
    background: THEME_CONFIG.glass,
    backdropFilter: 'blur(10px)',
    borderRadius: '50px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    flexWrap: 'wrap',
    maxWidth: '95vw',
  },
  navButton: {
    background: 'none',
    border: 'none',
    color: '#ffffff',
    padding: '0.6rem 1.2rem',
    cursor: 'pointer',
    fontSize: 'clamp(0.8rem, 1.5vw, 0.95rem)',
    fontWeight: '500',
  },
  section: {
    padding: 'clamp(3rem, 10vh, 8rem) 0',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 5%',
    width: '100%',
  },
  sectionTitle: {
    fontSize: 'clamp(2.5rem, 6vw, 4rem)',
    marginBottom: '2rem',
    textAlign: 'center',
    background: `linear-gradient(135deg, ${THEME_CONFIG.primary}, ${THEME_CONFIG.accent})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontWeight: 'bold'
  },
  projectSlider: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  sliderButton: {
    background: THEME_CONFIG.glass,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: THEME_CONFIG.primary,
    padding: '1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease'
  },
  projectCard: {
    flex: '1',
    minWidth: '300px',
    marginInline: 'auto',
    maxWidth: '650px',
    minHeight: '400px',
    background: THEME_CONFIG.glass,
    backdropFilter: 'blur(25px)',
    WebkitBackdropFilter: 'blur(25px)',
    padding: '0',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    cursor: 'pointer',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  projectImageContainer: {
    width: '100%',
    height: '160px',
    position: 'relative',
    overflow: 'hidden',
    borderBottom: `1px solid ${THEME_CONFIG.primary}30`,
  },
  projectImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease',
  },
  imageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(to bottom, transparent, ${THEME_CONFIG.background}80)`,
  },
  projectCardContent: {
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  projectTitle: {
    fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
    color: THEME_CONFIG.primary,
    marginBottom: '0.25rem',
    fontWeight: 'bold'
  },
  projectOrgTag: {
    color: THEME_CONFIG.accent,
    fontSize: '0.75rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '0.25rem',
    display: 'block'
  },
  projectSubtitle: {
    fontSize: 'clamp(1.1rem, 2.2vw, 1.4rem)',
    color: THEME_CONFIG.accent,
    marginBottom: '1rem'
  },
  projectShortDesc: {
    fontSize: '0.9rem',
    color: '#d0d0d0',
    marginBottom: '1rem',
    lineHeight: '1.4'
  },
  projectTechTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '1.5rem'
  },
  hashTag: {
    color: THEME_CONFIG.accent,
    marginRight: '12px',
    fontSize: '0.9rem',
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
    textDecorationColor: 'rgba(34, 211, 238, 0.3)',
    cursor: 'default',
    transition: '0.2s',
  },
  expandProjectBtn: {
    marginTop: 'auto', 
    alignSelf: 'flex-start',
    background: 'transparent',
    color: THEME_CONFIG.accent,
    border: `1px solid ${THEME_CONFIG.accent}`,
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.3s ease'
  },
  sliderIndicators: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.75rem',
    marginTop: '3rem'
  },
  indicator: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.3)',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  indicatorActive: {
    background: THEME_CONFIG.primary,
    width: '40px',
    borderRadius: '6px'
  },
  timeline: {
    position: 'relative',
    paddingLeft: 'clamp(1rem, 3vw, 2rem)'
  },
  timelineItem: {
    position: 'relative',
    paddingLeft: 'clamp(2rem, 4vw, 3rem)',
    paddingBottom: '3rem',
    borderLeft: `2px solid ${THEME_CONFIG.primary}40`,
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  timelineDot: {
    position: 'absolute',
    left: '-7px',
    top: '0',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: THEME_CONFIG.primary,
    boxShadow: `0 0 0 4px ${THEME_CONFIG.background}, 0 0 20px ${THEME_CONFIG.primary}60`
  },
  timelineCard: {
    background: THEME_CONFIG.glass,
    backdropFilter: 'blur(25px)',
    WebkitBackdropFilter: 'blur(25px)',
    padding: 'clamp(1.5rem, 3vw, 2rem)',
    borderRadius: '4px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  expTitle: {
    fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
    color: THEME_CONFIG.primary,
    marginBottom: '0.5rem',
    fontWeight: '600'
  },
  expOrg: {
    fontSize: 'clamp(1.1rem, 2.2vw, 1.3rem)',
    color: THEME_CONFIG.accent,
    marginBottom: '0.25rem'
  },
  expPeriod: {
    fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
    color: '#a0a0a0',
    marginBottom: '1rem'
  },
  expShortDesc: {
    fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
    color: '#d0d0d0',
    lineHeight: '1.6',
    marginBottom: '1rem'
  },
  viewDetailsBtn: {
    background: 'transparent',
    color: THEME_CONFIG.accent,
    border: 'none',
    fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
    cursor: 'pointer',
    padding: '0',
    fontWeight: '500'
  },
  skillsGrid: {
    display: 'grid',
    // Forces 3 columns on desktop, responsive min-width of 250px
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '25px',
    width: '100%',
    marginTop: '2rem',
  },
  skillCard: {
    background: THEME_CONFIG.glass,
    padding: '30px',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s ease',
  },
  skillCategory: {
    fontSize: '1.2rem',
    color: THEME_CONFIG.accent,
    marginBottom: '15px',
    fontWeight: '600',
    letterSpacing: '1px',
  },
  skillDivider: {
    height: '2px',
    width: '40px',
    background: THEME_CONFIG.primary,
    marginBottom: '20px',
  },
  bulletList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  bulletItem: {
    fontSize: '0.95rem',
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: '10px',
    lineHeight: '1.4',
  },
  achievementsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '2rem'
  },
  achievementCard: {
    background: THEME_CONFIG.glass,
    backdropFilter: 'blur(25px)',
    WebkitBackdropFilter: 'blur(25px)',
    padding: 'clamp(2rem, 3vw, 2.5rem)',
    borderRadius: '4px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    textAlign: 'center'
  },
  achievementYear: {
    display: 'inline-block',
    fontSize: 'clamp(1.5rem, 3vw, 2rem)',
    color: THEME_CONFIG.accent,
    fontWeight: 'bold',
    marginBottom: '0.5rem'
  },
  achievementPlace: {
    fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)',
    color: THEME_CONFIG.primary,
    fontWeight: 'bold',
    marginBottom: '1rem'
  },
  achievementEvent: {
    fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
    color: '#d0d0d0',
    lineHeight: '1.5'
  },
  contactSection: {
    background: THEME_CONFIG.glass,
    backdropFilter: 'blur(25px)',
    WebkitBackdropFilter: 'blur(25px)',
    padding: 'clamp(2rem, 4vw, 3rem)',
    borderRadius: '4px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    maxWidth: '700px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
    alignItems: 'center'
  },
  contactLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    width: '100%'
  },
  contactLinkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  contactLink: {
    fontSize: 'clamp(1rem, 2vw, 1.2rem)',
    color: '#d0d0d0',
    textDecoration: 'none',
    transition: 'color 0.3s ease'
  },
  sendMessageBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1rem 2rem',
    background: THEME_CONFIG.primary,
    border: 'none',
    borderRadius: '4px',
    fontSize: 'clamp(1rem, 2vw, 1.1rem)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  footer: {
    padding: '4rem 2rem 6rem 2rem',
    background: THEME_CONFIG.background,
  },
  footerDivider: {
    width: '100%',
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${THEME_CONFIG.primary}40, transparent)`,
    marginBottom: '3rem'
  },
  footerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '2rem'
  },
  footerLeft: {
    flex: '1',
    minWidth: '250px'
  },
  footerLogo: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: THEME_CONFIG.primary,
    marginBottom: '0.5rem',
    letterSpacing: '2px'
  },
  footerTagline: {
    color: '#a0a0a0',
    fontSize: '0.9rem',
    maxWidth: '300px',
    lineHeight: '1.5'
  },
  footerRight: {
    textAlign: 'right',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '1rem'
  },
  footerSocials: {
    display: 'flex',
    gap: '1.5rem'
  },
  footerLink: {
    color: '#ffffff',
    textDecoration: 'none',
    fontSize: '0.9rem',
    transition: 'color 0.3s ease',
    cursor: 'pointer',
    fontWeight: '500'
  },
  copyright: {
    color: '#666666',
    fontSize: '0.8rem',
    marginTop: '0.5rem'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '2rem',
    overflowY: 'auto'
  },
  modalContent: {
    scale: 1,
    opacity: 1,
    background: '#151921',
    maxHeight: '85vh',
    width: '90%',
    maxWidth: '700px',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative'
  },
  modalHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '2rem',
    background: 'rgba(21, 25, 33, 0.95)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  modalTitle: {
    fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
    color: THEME_CONFIG.primary,
    marginBottom: '0.5rem',
    fontWeight: 'bold'
  },
  modalSubtitle: {
    fontSize: 'clamp(1.1rem, 2.2vw, 1.4rem)',
    color: THEME_CONFIG.accent
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: '#ffffff',
    cursor: 'pointer',
    padding: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'all 0.3s ease'
  },
  modalBody: {
    padding: '2rem',
    overflowY: 'auto',
    flex: 1,
    maxHeight: 'calc(85vh - 100px)'
  },
  modalPeriod: {
    fontSize: 'clamp(1rem, 2vw, 1.15rem)',
    color: '#a0a0a0',
    marginBottom: '0.5rem'
  },
  modalLocation: {
    fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
    color: '#b0b0b0',
    marginBottom: '2rem'
  },
  projectFullDesc: {
    fontSize: 'clamp(1rem, 2vw, 1.15rem)',
    color: '#d0d0d0',
    lineHeight: '1.8',
    marginBottom: '2rem'
  },
  detailsHeading: {
    fontSize: 'clamp(1.3rem, 2.5vw, 1.6rem)',
    color: THEME_CONFIG.accent,
    marginTop: '2rem',
    marginBottom: '1rem',
    fontWeight: '600'
  },
  detailsList: {
    paddingLeft: '1.5rem',
    margin: 0
  },
  detailItem: {
    fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
    color: '#d0d0d0',
    lineHeight: '1.8',
    marginBottom: '0.75rem'
  },
  inlineImageContainer: {
    margin: '1.5rem 0',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  },
  inlineImage: {
    width: '100%',
    display: 'block'
  },
  imageCaption: {
    display: 'block',
    padding: '0.5rem',
    fontSize: '0.85rem',
    color: '#a0a0a0',
    textAlign: 'center',
    background: 'rgba(0,0,0,0.2)'
  },
  appendixContainer: {
    marginTop: '2rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
  },
  appendixToggle: {
    width: '100%',
    padding: '1.5rem 0',
    background: 'none',
    border: 'none',
    color: THEME_CONFIG.primary,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '1.1rem',
    fontWeight: '600'
  },
  appendixGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1rem',
    paddingBottom: '2rem'
  },
  appendixItem: {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '4px',
    padding: '0.5rem'
  },
  appendixImage: {
    width: '100%',
    aspectRatio: '1/1',
    objectFit: 'cover',
    borderRadius: '2px'
  },
  appendixImageTitle: {
    fontSize: '0.8rem',
    marginTop: '0.5rem',
    color: '#d0d0d0'
  },
  expLogo: {
    width: '50px',
    height: '50px',
    borderRadius: '4px',
    backgroundColor: 'white',
    padding: '4px',
    objectFit: 'contain',
  },
  dynamicContent: {
    marginTop: '1rem'
  }
};

export default App;